import * as React from 'react';

import { paragraph } from '@gitbook/doc-core';
import { formatMarkdown, parseMarkdown } from '@gitbook/doc-format';
import { useHiveWorkingDocument } from '@gitbook/hive-react';
import {
    BlockRange,
    type Change,
    Document,
    type SelectionRange,
    type Text,
    TextRange,
    type Value,
} from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { type ReadStopwatch, reportException, startStopwatch } from '@gitbook/util-debug';
import { useDebouncedFlush, useEventCallback, useLiveRef } from '@gitbook/util-react-hooks';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { shortcuts, useKeyboardShortcuts } from '../../../Keyboard';
import { clearSelectionHighlighting, highlightSelection } from '../common';
import { ContentEditorContext, type ContentEditorContextValue } from '../contexts';

export type CompletionSelection = {
    range: SelectionRange;
    startKey: string;
    endKey: string;
};

export type CompletionOperation = 'insert' | 'replace';

type CompletionHistoryEntry = {
    completionId: string;
    prompt: string;
    result: string;
    resultDocument: Document;
    history: CompletionHistory;
};

export type CompletionHistory = CompletionHistoryEntry[];

export type CompletionState = {
    /** Status of completion. */
    status: 'idle' | 'started' | 'running' | 'stopping' | 'done';
    /** Unique id of the completion. */
    completionId: string | null;
    /** Operation applied. */
    operation: CompletionOperation | null;
    /** Selection representing the required or inserted text / blocks. */
    selection: CompletionSelection | null;
    /** Prompt of completion. */
    prompt: string | null;
    /** Result from completion, only for "replace" operation. */
    result: string | null;
    /** Result from completion as document, for both operation. */
    resultDocument: Document | null;
    /** Stopwatch attached when running. */
    getExecutionTime: ReadStopwatch | null;
    /** History of completions. */
    history: CompletionHistory;
    /** Pointer in history */
    historyIndex: number | null;
};

type CompletionAction =
    | {
          type: 'open';
          selection: CompletionSelection;
          operation?: CompletionOperation;
      }
    | {
          type: 'run';
          prompt: string;
          completionId: string;
      }
    | {
          type: 'setSelection';
          selection: CompletionSelection;
      }
    | {
          type: 'setResult';
          result: string;
      }
    | {
          type: 'complete';
          result: string;
          resultDocument: Document;
      }
    | {
          type: 'close';
      }
    | {
          type: 'stop';
      }
    | {
          type: 'goToHistory';
          index: number;
          selection?: CompletionSelection;
      };

export type OpenTrigger =
    | 'keyboard-space'
    | 'block-button'
    | 'block-options'
    | 'commands-palette'
    | 'hovering-menu'
    | 'keyboard-mod-j';
export type RunTrigger = 'keyboard-enter' | 'palette-command' | 'submit-button' | 'retry';
export type StopTrigger = 'keyboard-escape';
export type RetryTrigger = 'keyboard-alt-r' | 'palette-command';

export type CompletionApi = {
    open: (args: { trigger: OpenTrigger; selectionRange?: SelectionRange }) => void;
    close: () => void;
    run: (args: { prompt: string; trigger: RunTrigger; completionId?: string }) => void;
    stop: (args: { trigger: StopTrigger }) => void;
    retry: (args: { trigger: RetryTrigger }) => string | null;
    goToHistory: (index: number) => void;
    replaceSelection: () => void;
    insertBelowSelection: () => void;
};

const CompletionStateContext = React.createContext<CompletionState | null>(null);
const CompletionApiContext = React.createContext<CompletionApi | null>(null);

function generateId() {
    return Math.random().toString(36).substring(2, 9);
}

function checkAreSelectionsEqual(a: CompletionSelection, b: CompletionSelection): boolean {
    return a.startKey === b.startKey && a.endKey === b.endKey && a.range.equals(b.range);
}

const initialState: CompletionState = {
    completionId: null,
    operation: null,
    status: 'idle',
    selection: null,
    prompt: null,
    result: null,
    resultDocument: null,
    getExecutionTime: null,
    history: [],
    historyIndex: null,
};

function getOperationFromSelection(selection: CompletionSelection): CompletionOperation {
    // If it's a collapsed text range, then we are in insert mode.
    if (TextRange.isTextRange(selection.range) && selection.range.isCollapsed) {
        return 'insert';
    }
    return 'replace';
}

function reducer(state: CompletionState, action: CompletionAction): CompletionState {
    switch (action.type) {
        case 'open':
            return {
                status: 'started',
                completionId: generateId(),
                operation: action.operation ?? getOperationFromSelection(action.selection),
                selection: action.selection,
                prompt: null,
                result: null,
                resultDocument: null,
                getExecutionTime: null,
                history: [],
                historyIndex: null,
            };
        case 'close': {
            return {
                status: 'idle',
                completionId: null,
                operation: null,
                selection: null,
                prompt: null,
                result: null,
                resultDocument: null,
                getExecutionTime: null,
                history: [],
                historyIndex: null,
            };
        }
        case 'run': {
            return {
                status: 'running',
                completionId: action.completionId,
                operation: state.operation,
                selection: state.selection,
                prompt: action.prompt,
                result: null,
                resultDocument: null,
                getExecutionTime: startStopwatch(),
                history: state.history,
                historyIndex: state.historyIndex,
            };
        }
        case 'stop': {
            return {
                status: 'stopping',
                completionId: state.completionId,
                operation: state.operation,
                selection: state.selection,
                prompt: state.prompt,
                result: state.result,
                resultDocument: state.resultDocument,
                getExecutionTime: null,
                history: state.history,
                historyIndex: state.historyIndex,
            };
        }
        case 'setSelection': {
            // If the selection is the same, then we don't need to update the state.
            if (state.selection && checkAreSelectionsEqual(state.selection, action.selection)) {
                return state;
            }
            return {
                status: state.status,
                completionId: state.completionId,
                operation: state.operation,
                selection: action.selection,
                prompt: state.prompt,
                getExecutionTime: state.getExecutionTime,
                result: null,
                resultDocument: null,
                history: state.history,
                historyIndex: state.historyIndex,
            };
        }
        case 'setResult': {
            return {
                status: state.status,
                completionId: state.completionId,
                operation: state.operation,
                selection: state.selection,
                prompt: state.prompt,
                result: action.result,
                resultDocument: state.resultDocument,
                getExecutionTime: state.getExecutionTime,
                history: state.history,
                historyIndex: state.historyIndex,
            };
        }
        case 'complete': {
            assertIsDefined(state.prompt);
            assertIsDefined(state.completionId);
            assertIsDefined(state.selection);
            // Take the entry used for the completion.
            const usedEntry =
                state.historyIndex !== null ? state.history[state.historyIndex] : null;
            // Compute a new history that includes the used entry.
            const entryHistory: CompletionHistory = usedEntry
                ? [...usedEntry.history, usedEntry]
                : state.history;
            // Create a new entry that will be added to the global history.
            const entry: CompletionHistoryEntry = {
                completionId: state.completionId,
                prompt: state.prompt,
                resultDocument: action.resultDocument,
                result: action.result,
                history: entryHistory,
            };
            const history: CompletionHistory = [...state.history, entry];
            return {
                status: 'done',
                completionId: generateId(),
                operation: state.operation,
                selection: state.selection,
                prompt: state.prompt,
                result: state.result,
                resultDocument: action.resultDocument,
                getExecutionTime: null,
                history,
                historyIndex: state.history.length,
            };
        }
        case 'goToHistory': {
            const { index } = action;
            const entry = state.history[index];
            assertIsDefined(entry);
            return {
                ...state,
                historyIndex: index,
                prompt: entry.prompt,
                resultDocument: entry.resultDocument,
                selection: action.selection ?? state.selection,
            };
        }
        default: {
            assertNever(action);
        }
    }
}

function getBlockKeysFromRange(
    document: Document,
    range: SelectionRange
): { startKey: string; endKey: string } | null {
    if (TextRange.isTextRange(range)) {
        if (!range.startKey || !range.endKey) {
            return null;
        }

        if (!document.hasNode(range.startKey) || !document.hasNode(range.endKey)) {
            return null;
        }

        const startBlock = document.getClosestBlock(range.startKey);
        if (!startBlock) {
            return null;
        }

        const endBlock = document.getClosestBlock(range.endKey);
        if (!endBlock) {
            return null;
        }

        return { startKey: startBlock.key, endKey: endBlock.key };
    }

    if (!range.anchorBlock || !range.focusBlock) {
        return null;
    }

    if (!document.hasNode(range.anchorBlock) || !document.hasNode(range.focusBlock)) {
        return null;
    }

    const isForward = document.areDescendantsSorted(range.anchorBlock, range.focusBlock);

    return {
        startKey: isForward ? range.anchorBlock : range.focusBlock,
        endKey: isForward ? range.focusBlock : range.anchorBlock,
    };
}

function getCompletionSelection(
    document: Document,
    range: SelectionRange
): CompletionSelection | null {
    const keys = getBlockKeysFromRange(document, range);

    if (!keys) {
        return null;
    }

    return {
        range,
        startKey: keys.startKey,
        endKey: keys.endKey,
    };
}

function replaceSelectionWithDocument(args: {
    change: Change;
    document: Document;
    selection: SelectionRange;
}) {
    const { change, document } = args;

    if (!change.value.selection.equals(args.selection)) {
        change.select(args.selection);
    }
    change.insertDocument(document);
}

function insertDocumentBelowSelection(args: {
    change: Change;
    document: Document;
    endKey: string;
}) {
    const { change, document, endKey } = args;

    const block = change.value.document.getByKey(endKey);
    if (!block) {
        return;
    }
    const emptyBlock = paragraph.createParagraph();
    change.insertNodeAfterByKey(endKey, emptyBlock);
    change.select(BlockRange.createAtBlock(emptyBlock));
    change.insertDocument(document);
}

function CompletionContextProviderWithDeps(props: {
    children: React.ReactNode;
    editorValue: Value;
    editorRef: React.RefObject<EditorContainer>;
    logAnalyticsEvent: ContentEditorContextValue['logAnalyticsEvent'];
}): React.ReactElement {
    const { logAnalyticsEvent, editorValue, editorRef } = props;

    const hiveWorkingDocument = useHiveWorkingDocument();
    assertIsDefined(hiveWorkingDocument);

    const [state, dispatch] = React.useReducer(reducer, initialState);
    const stateRef = useLiveRef(state);

    const isInsertRunningOrStopping =
        (state.status === 'running' || state.status === 'stopping') && state.operation === 'insert';

    // Debounced selection updates to prevent flashing during streaming
    const debouncedUpdateSelection = useDebouncedFlush(500);

    const updateCompletionSelection = React.useCallback(() => {
        // Find completion nodes with the same completion ID.
        const completionBlocks = editorValue.document
            .getBlocks()
            .filter((node) => node.data.get('completionId') === state.completionId);

        // Get the first and last nodes of the completion.
        const first = completionBlocks.first();
        const last = completionBlocks.last();

        if (!first || !last) {
            return;
        }

        // Create a new block range from it.
        const range = BlockRange.create({
            anchorBlock: first.key,
            focusBlock: last.key,
        });

        const selection = getCompletionSelection(editorValue.document, range);

        if (!selection) {
            return;
        }

        dispatch({ type: 'setSelection', selection });

        assertIsDefined(editorRef.current);
        editorRef.current.change((change) => {
            if (selection && !change.value.selection.equals(selection.range)) {
                change.select(selection.range).focus();
            }
        });
    }, [editorValue.document, state.completionId, editorRef]);

    React.useLayoutEffect(() => {
        // If the completion is running, then we want to update the selection to the latest
        // selection of the completion.
        if (!isInsertRunningOrStopping) {
            // Cancel any pending debounced updates when not streaming
            debouncedUpdateSelection.cancel();
            return;
        }

        // During streaming, use debounced updates to prevent flashing
        debouncedUpdateSelection(() => {
            updateCompletionSelection();
        });
    }, [
        isInsertRunningOrStopping,
        editorValue.document,
        state.completionId,
        debouncedUpdateSelection,
        updateCompletionSelection,
    ]);

    // Highlight the selection when it's open.
    const { selection } = state;
    React.useEffect(() => {
        assertIsDefined(editorRef.current);
        editorRef.current.change((change) => {
            if (selection && TextRange.isTextRange(selection.range)) {
                highlightSelection(change, selection.range);
            } else {
                clearSelectionHighlighting(change);
            }
        });
    }, [selection, editorRef]);

    useKeyboardShortcuts(
        {
            [shortcuts.EDITOR_AI]: () => {
                open({ trigger: 'keyboard-mod-j' });
            },
        },
        'editor-selection-expanded'
    );

    const open: CompletionApi['open'] = useEventCallback(
        ({ trigger, selectionRange = editorValue.selection }) => {
            const selection = getCompletionSelection(editorValue.document, selectionRange);
            if (!selection) {
                return;
            }

            logAnalyticsEvent({
                type: 'edit_start_block_completion_prompt',
                trigger,
            });
            dispatch({ type: 'open', selection });
        }
    );

    const close: CompletionApi['close'] = useEventCallback(() => {
        const { selection } = state;
        assertIsDefined(editorRef.current);
        editorRef.current.change((change) => {
            if (selection) {
                change.select(selection.range);
            }
            change.focus();
        });
        dispatch({ type: 'close' });
    });

    const markAsComplete = useEventCallback(async (finalResult: string | null = null) => {
        assertIsDefined(state.operation);
        switch (state.operation) {
            case 'insert': {
                assertIsDefined(state.selection);
                const resultDocument = BlockRange.isBlockRange(state.selection.range)
                    ? Document.create({
                          nodes: state.selection.range.getBlocks(editorValue.document),
                      })
                    : Document.create({ nodes: [] });
                const result = await formatMarkdown(resultDocument);
                dispatch({ type: 'complete', resultDocument, result });
                return;
            }
            case 'replace': {
                // Use the finalResult if provided (from streaming), otherwise fall back to state
                const result = finalResult ?? state.result ?? '';
                const { document } = await parseMarkdown(result);
                dispatch({ type: 'complete', resultDocument: document, result });
                return;
            }
            default: {
                assertNever(state.operation);
            }
        }
    });

    const run: CompletionApi['run'] = useEventCallback((args) => {
        const { prompt, trigger } = args;
        const { operation, selection } = state;
        const completionId = args.completionId ?? state.completionId;

        assertIsDefined(operation);
        assertIsDefined(completionId);
        assertIsDefined(selection);

        dispatch({ type: 'run', prompt, completionId });

        logAnalyticsEvent({
            type: 'edit_ai_completion_submit',
            completionId,
            prompt,
            operation,
            trigger,
        });

        function checkIsRelevant() {
            return (
                stateRef.current.status === 'running' &&
                stateRef.current.completionId === completionId
            );
        }

        const entry = state.historyIndex !== null ? state.history[state.historyIndex] : null;
        const requestHistory = (entry ? [...entry.history, entry] : []).map((entry) => ({
            prompt: entry.prompt,
            answer: entry.result,
        }));

        switch (operation) {
            case 'insert': {
                hiveWorkingDocument.completions
                    .generateBlockCompletion({
                        id: completionId,
                        startKey: selection.startKey,
                        endKey: selection.endKey,
                        prompt,
                        history: requestHistory,
                    })
                    .then(async () => {
                        if (checkIsRelevant()) {
                            assertIsDefined(stateRef.current.selection);
                            assertIsDefined(stateRef.current.getExecutionTime);
                            logAnalyticsEvent({
                                type: 'edit_ai_completion_complete',
                                completionId,
                                prompt,
                                executionTime: stateRef.current.getExecutionTime(),
                            });
                            await markAsComplete();
                        }
                    })
                    .catch((error) => {
                        reportException(error, {
                            extra: {
                                completionId: state.completionId,
                                prompt,
                            },
                        });

                        if (checkIsRelevant()) {
                            close();
                        }
                    });
                return;
            }
            case 'replace': {
                const startStreaming = async () => {
                    const input = await (async () => {
                        assertIsDefined(editorRef.current);
                        if (BlockRange.isBlockRange(selection.range)) {
                            const nodes = selection.range.getBlocks(
                                editorRef.current.value.document
                            );
                            if (nodes.size === 0) {
                                return null;
                            }
                            return formatMarkdown(Document.create({ nodes }));
                        }
                        const texts = getTextsFromSelection(
                            selection.range,
                            editorRef.current.value.document
                        );
                        if (!texts.length) {
                            return null;
                        }
                        const markdown = await formatMarkdown(
                            Document.create({ nodes: [paragraph.createParagraph(texts)] })
                        );
                        // Remove end "\n" created from paragraph
                        return markdown.replace(/\n$/, '');
                    })();

                    // Without relevant input, selection is outdated, we close.
                    if (!input) {
                        close();
                        return null;
                    }

                    const iterable = hiveWorkingDocument.streamAICompletion({
                        id: completionId,
                        input,
                        prompt,
                        history: requestHistory,
                    });
                    let result = '';
                    for await (const chunk of iterable) {
                        if (checkIsRelevant()) {
                            result += chunk.completion;
                            dispatch({ type: 'setResult', result });
                        }
                    }

                    // Ensure that the final result is properly set in state
                    if (result) {
                        dispatch({ type: 'setResult', result });
                    }

                    return result;
                };

                startStreaming()
                    .then(async (finalResult) => {
                        if (checkIsRelevant()) {
                            assertIsDefined(stateRef.current.getExecutionTime);
                            logAnalyticsEvent({
                                type: 'edit_ai_completion_complete',
                                completionId,
                                prompt,
                                executionTime: stateRef.current.getExecutionTime(),
                            });
                            await markAsComplete(finalResult);
                        }
                    })
                    .catch((error) => {
                        reportException(error, {
                            extra: {
                                completionId: state.completionId,
                                prompt,
                            },
                        });

                        if (checkIsRelevant()) {
                            close();
                        }
                    });
                return;
            }
            default:
                assertNever(operation);
        }
    });

    const stop: CompletionApi['stop'] = useEventCallback(({ trigger }) => {
        if (state.status !== 'running') {
            return;
        }

        assertIsDefined(state.operation);
        assertIsDefined(state.completionId);
        assertIsDefined(state.prompt);
        assertIsDefined(state.getExecutionTime);

        logAnalyticsEvent({
            type: 'edit_ai_completion_stop',
            completionId: state.completionId,
            operation: state.operation,
            prompt: state.prompt,
            trigger,
            runTime: state.getExecutionTime(),
        });

        dispatch({ type: 'stop' });

        const { completionId } = state;

        function checkIsRelevant() {
            return (
                stateRef.current.status === 'stopping' &&
                stateRef.current.completionId === completionId
            );
        }

        hiveWorkingDocument.completions
            .stopAICompletion({ id: completionId })
            .then(async () => {
                if (checkIsRelevant()) {
                    await markAsComplete();
                }
            })
            .catch((error) => {
                reportException(error, {
                    extra: {
                        completionId: state.completionId,
                        prompt: state.prompt,
                    },
                });

                if (checkIsRelevant()) {
                    close();
                }
            });
    });

    const retry: CompletionApi['retry'] = useEventCallback((args) => {
        assertIsDefined(state.operation);
        assertIsDefined(state.prompt);

        const completionId = generateId();

        logAnalyticsEvent({
            type: 'edit_ai_completion_retry',
            completionId,
            operation: state.operation,
            prompt: state.prompt,
            trigger: args.trigger,
        });

        // If a completion is not running, we try to find the previous entry and fills the prompt with it.
        if (state.status !== 'running') {
            const currentEntry =
                state.historyIndex !== null ? state.history[state.historyIndex] : null;
            const previousEntry = currentEntry
                ? currentEntry.history[currentEntry.history.length - 1]
                : null;

            if (currentEntry && previousEntry) {
                const previousEntryIndex = state.history.indexOf(previousEntry);
                goToHistory(previousEntryIndex);
                return currentEntry.prompt;
            }
        }

        // In other cases, we just retry the current prompt.
        run({ prompt: state.prompt, trigger: 'retry', completionId });
        return null;
    });

    const replaceSelection: CompletionApi['replaceSelection'] = useEventCallback(() => {
        const { resultDocument, selection } = state;
        assertIsDefined(resultDocument);
        assertIsDefined(selection);

        assertIsDefined(editorRef.current);
        editorRef.current.change((change) => {
            replaceSelectionWithDocument({
                change,
                document: resultDocument,
                selection: selection.range,
            });
            change.focus();
        });
    });

    const insertBelowSelection: CompletionApi['insertBelowSelection'] = useEventCallback(() => {
        const { resultDocument, selection } = state;
        assertIsDefined(resultDocument);
        assertIsDefined(selection);

        assertIsDefined(editorRef.current);
        editorRef.current.change((change) => {
            insertDocumentBelowSelection({
                change,
                document: resultDocument,
                endKey: selection.endKey,
            });
            change.focus();
        });
    });

    const goToHistory: CompletionApi['goToHistory'] = useEventCallback((index) => {
        assertIsDefined(state.operation);
        switch (state.operation) {
            case 'insert': {
                const entry = state.history[index];
                assertIsDefined(entry);
                assertIsDefined(editorRef.current);
                editorRef.current.change((change) => {
                    replaceSelectionWithDocument({
                        change,
                        document: entry.resultDocument,
                        selection: change.value.selection,
                    });
                    const selection = getCompletionSelection(
                        change.value.document,
                        change.value.selection
                    );
                    if (selection) {
                        dispatch({ type: 'goToHistory', index, selection });
                    } else {
                        close();
                    }
                });
                return;
            }
            case 'replace': {
                dispatch({ type: 'goToHistory', index });
                return;
            }
            default:
                assertNever(state.operation);
        }
    });

    const api = React.useMemo<CompletionApi>(
        () => ({
            open,
            close,
            run,
            stop,
            retry,
            replaceSelection,
            insertBelowSelection,
            goToHistory,
        }),
        [open, close, run, stop, retry, replaceSelection, insertBelowSelection, goToHistory]
    );

    return (
        <CompletionStateContext.Provider value={state}>
            <CompletionApiContext.Provider value={api}>
                {props.children}
            </CompletionApiContext.Provider>
        </CompletionStateContext.Provider>
    );
}

export function CompletionContextProvider(props: {
    children: React.ReactNode;
    editorValue: Value;
    editorRef: React.RefObject<EditorContainer>;
    readOnly: boolean;
}) {
    const {
        logAnalyticsEvent,
        blocks: { withCompletions },
    } = React.useContext(ContentEditorContext);

    if (!withCompletions || props.readOnly) {
        return props.children as unknown as React.ReactElement;
    }

    return (
        <CompletionContextProviderWithDeps
            logAnalyticsEvent={logAnalyticsEvent}
            editorRef={props.editorRef}
            editorValue={props.editorValue}
        >
            {props.children}
        </CompletionContextProviderWithDeps>
    );
}

export function useCompletionState(): CompletionState | null {
    return React.useContext(CompletionStateContext);
}

export function useCompletionApi(): CompletionApi | null {
    return React.useContext(CompletionApiContext);
}

function getTextsFromSelection(range: TextRange, document: Document) {
    const texts = document.getTextsAtRange(range);
    return document.getTextsAtRange(range).reduce((cuted, text, index) => {
        if (index === texts.size - 1) {
            text = text.removeText(range.endOffset, text.text.length - range.endOffset);
        }
        if (index === 0) {
            text = text.sliceText(range.startOffset);
        }
        cuted.push(text);
        return cuted;
    }, [] as Text[]);
}
